import React, { useId, useLayoutEffect, useRef, useState } from "react";

/**
 * Operator-entered text in a table cell: clamped to a few lines, expandable only when the clamp
 * really hides something.
 *
 * PRESENTATIONAL, like `LoadFailure`: the caller resolves both labels, so this file imports no
 * dictionary. The text is a React text child (never HTML) and keeps the author's line breaks.
 * Unbroken tokens wrap with `overflow-wrap: anywhere`, not `break-words`: both wrap on screen,
 * but `break-word` still counts the whole token as the element's min-content width, so a
 * 500-character token pins a table column at its max-width and pushes the table past the
 * viewport. `anywhere` lets the column shrink; text with spaces lays out the same either way.
 *
 * Overflow is MEASURED (`scrollHeight > clientHeight + 1` while collapsed), after layout, on
 * every size change, and whenever a web font finishes loading: a font swap can move the text
 * from N to N+1 lines inside a clamped box whose size does not change, which no
 * ResizeObserver reports. A character count cannot know the cell's width or the font.
 *
 * The clamp classes are literal in a map because Tailwind's JIT only emits class names it can
 * see in the source.
 */
const CLAMP = { 2: "line-clamp-2", 3: "line-clamp-3" };

export default function ExpandableText({
  text, lines = 3, moreLabel, lessLabel, className = "", testId,
}) {
  const clamp = CLAMP[lines];
  if (!clamp) throw new Error(`ExpandableText: unsupported lines=${lines} (use ${Object.keys(CLAMP).join(" or ")})`);

  const id = useId();
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const hasText = Boolean(text && text.trim());

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return undefined;
    const measure = () => setOverflowing(el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (observer) observer.observe(el);
    const fonts = typeof document === "undefined" ? undefined : document.fonts;
    if (fonts) fonts.addEventListener("loadingdone", measure);
    return () => {
      if (observer) observer.disconnect();
      if (fonts) fonts.removeEventListener("loadingdone", measure);
    };
  }, [text, lines, expanded, hasText]);

  if (!hasText) return null;

  return (
    <>
      <div
        ref={ref}
        id={id}
        data-testid={testId}
        className={`whitespace-pre-wrap [overflow-wrap:anywhere] ${expanded ? "" : clamp} ${className}`.trim()}
      >
        {text}
      </div>
      {(overflowing || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls={id}
          data-testid={testId ? `${testId}-toggle` : undefined}
          className="text-xs font-medium text-slate-600 underline underline-offset-2 hover:text-slate-900 mt-0.5"
        >
          {expanded ? lessLabel : moreLabel}
        </button>
      )}
    </>
  );
}
